import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { ISearchQueryResponse } from 'src/api/queries';
import ReviewerInformation from './ReviewerInformation';
import { Badge, ButtonIcon, ButtonText } from '@morressier/alembic';
import { ILexiconField } from '@publishing-engine/domain/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDoubleDown,
  faAngleDoubleUp
} from '@fortawesome/pro-solid-svg-icons';
import { useState, useMemo, useRef } from 'react';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import { UseFormWatch } from 'react-hook-form';
import Loading from 'src/pages/Status/Loading';
import NoSearchResultFound from './NoSearchResultFound';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

const DEFAULT_SORT = 'ReviewerFirstName_ASC';

const SORT_FIELDS: SortField[] = [
  { value: 'ReviewerFirstName', label: 'First Name (A–Z)', direction: 'asc' },
  { value: 'ReviewerFirstName', label: 'First Name (Z–A)', direction: 'desc' },
  { value: 'RELEVANCE', label: 'Relevance', direction: 'asc' },
  {
    value: 'AverageRScore',
    label: 'Avg. R Score (High to Low)',
    direction: 'desc'
  },
  {
    value: 'AverageRScore',
    label: 'Avg. R Score (Low to High)',
    direction: 'asc'
  },
  {
    value: 'ActiveReviews',
    label: 'Active Reviews (High to Low)',
    direction: 'desc'
  },
  {
    value: 'ActiveReviews',
    label: 'Active Reviews (Low to High)',
    direction: 'asc'
  }
];

const sortValueToField = (sortValue: string): SortField | null => {
  const field = SORT_FIELDS.find(
    f => `${f.value}_${f.direction.toUpperCase()}` === sortValue
  );
  return field ?? null;
};

const fieldToSortValue = (field: SortField): string => {
  return `${field.value}_${field.direction.toUpperCase()}`;
};

interface FilterGroup {
  key: string;
  label: string;
  items: { value: string; name: string }[];
}

type IActiveFilterChip = {
  key: string;
  label: string;
  value: string;
};
interface Props {
  searchResults: ISearchQueryResponse | undefined;
  sortValue: string;
  setSortValue: (value: string) => void;
  reviewerConfig?: ILexiconField[];
  globalLexiconConfig: boolean | undefined;
  filterGroups?: FilterGroup[];
  watch?: UseFormWatch<any>;
  onFiltersSubmit?: (values: Record<string, string[]>) => void;
  selectedFilters?: Record<string, string[]>;
  setSelectedFilters?: (filters: Record<string, string[]>) => void;
  isFetching: boolean;
  onReset: () => void;
  activeFilterChips: IActiveFilterChip[];
}

const SearchResults = ({
  searchResults,
  sortValue,
  setSortValue,
  reviewerConfig,
  globalLexiconConfig,
  filterGroups,
  watch,
  onFiltersSubmit,
  selectedFilters,
  setSelectedFilters,
  isFetching,
  onReset,
  activeFilterChips
}: Props) => {
  const [expandAll, setExpandAll] = useState<boolean>(true);
  const loaderRef = useRef<HTMLDivElement | null>(null);

  const handleExpandAll = () => {
    setExpandAll(prev => !prev);
  };

  const appliedSort = useMemo(() => sortValueToField(sortValue), [sortValue]);

  const sortOptions = useMemo(() => ({ fields: SORT_FIELDS }), []);

  const handleApplySort = (selected: SortField | null) => {
    if (selected) {
      setSortValue(fieldToSortValue(selected));
    }
  };

  const handleResetSort = () => {
    setSortValue(DEFAULT_SORT);
  };

  const showFilterDrawer =
    filterGroups &&
    filterGroups.length > 0 &&
    watch &&
    onFiltersSubmit &&
    selectedFilters &&
    setSelectedFilters &&
    ((searchResults?.total ?? 0) > 0 || activeFilterChips?.length > 0);

  return (
    <Flex direction="column" padding={1} my={4} ref={loaderRef}>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex alignItems="center">
          <Text textStyle="textBody">
            Showing <strong>{searchResults?.total}</strong> Reviewers
          </Text>
          <Tooltip
            label={expandAll ? 'Collapse All' : 'Expand All'}
            placement="top"
            hasArrow
          >
            <Box>
              <ButtonIcon
                ariaLabel={expandAll ? 'Collapse All' : 'Expand All'}
                icon={
                  <FontAwesomeIcon
                    icon={!expandAll ? faAngleDoubleDown : faAngleDoubleUp}
                  />
                }
                onClick={handleExpandAll}
              />
            </Box>
          </Tooltip>
        </Flex>
        <Flex alignItems="center" gap={2}>
          {showFilterDrawer && (
            <>
              <FilterDrawer
                filterGroups={filterGroups}
                watch={watch}
                onSubmit={onFiltersSubmit}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
              />

              <SortMenu
                appliedSort={appliedSort}
                options={sortOptions}
                onApply={handleApplySort}
                onReset={handleResetSort}
              />
            </>
          )}{' '}
        </Flex>
      </Flex>
      {activeFilterChips.length > 0 && (
        <Flex mt={3} alignItems={'center'} gap={2}>
          <Flex
            gap={2}
            wrap="wrap"
            paddingBottom={2}
            backgroundColor="surfacePrimaryFill"
            flex={1}
          >
            {activeFilterChips.map(filterChip => (
              <Badge
                variant="interactive"
                key={filterChip.key}
                paddingX={3}
                paddingY={1}
                borderRadius="md"
                colorScheme="gray"
                textTransform="none"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {`${filterChip.label}: ${filterChip.value}`}
              </Badge>
            ))}
          </Flex>

          <ButtonText
            mt={-1.5}
            maxHeight={'max-content'}
            variant="caution"
            onClick={onReset}
          >
            <FontAwesomeIcon icon={faXmark} /> Clear Filter
          </ButtonText>
        </Flex>
      )}
      <Box my={3}></Box>
      <div />
      {isFetching ? (
        <Box>
          <Loading />
        </Box>
      ) : (
        <Flex flexDirection="column">
          {searchResults?.total ? (
            searchResults?.items.map(item => (
              <ReviewerInformation
                expandAll={expandAll}
                item={item}
                globalLexiconConfig={globalLexiconConfig}
                reviewerConfig={reviewerConfig}
              />
            ))
          ) : (
            <NoSearchResultFound />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default SearchResults;
